import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "../../api/client";
import { launchTypesApi } from "../../api/endpoints";
import type { CardType, LaunchTypeCard, LaunchTypeDay } from "../../types/domain";
import { IcoCheck, IcoClock, IcoClose, IcoCopy, IcoTrash } from "../../components/icons";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { MdEditor } from "../../components/MdEditor";
import { mdToHtml } from "../../lib/md";
import { useCanWrite } from "../../hooks/useCanWrite";
import { ConflictDialog, type ConflictFieldDiff } from "../../components/ConflictDialog";

type State =
  | { kind: "create"; dayKey: string }
  | { kind: "edit"; card: LaunchTypeCard };

interface Props {
  launchTypeId: string;
  state: State;
  days: LaunchTypeDay[];
  types: CardType[];
  onClose: () => void;
  onSaved: () => void;
}

export function LaunchTypeCardModal({ launchTypeId, state, days, types, onClose, onSaved }: Props) {
  const canWrite = useCanWrite();
  const readOnly = !canWrite;
  const isNew = state.kind === "create";

  // Дефолт для новой карточки — первый в `types` (сервер сортирует по
  // sortOrder). Пользователь управляет порядком в TypesModal стрелками ↑/↓.
  const initial =
    state.kind === "edit"
      ? {
          dayKey: state.card.dayKey,
          typeId: state.card.typeId,
          title: state.card.title,
          preview: state.card.preview,
          time: state.card.time ?? "",
        }
      : {
          dayKey: state.dayKey,
          typeId: types[0]?.id ?? "",
          title: "",
          preview: "",
          time: "",
        };

  const [draft, setDraft] = useState(initial);
  // Optimistic-lock baseline: фиксируем updatedAt на открытии, чтобы PATCH
  // с чужими правками был отвергнут 409-м.
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<string | null>(
    state.kind === "edit" ? state.card.updatedAt : null,
  );
  const [conflict, setConflict] = useState<LaunchTypeCard | null>(null);
  const [patchError, setPatchError] = useState<Error | null>(null);

  // Если modal открыт до загрузки `types`, draft.typeId = "" и остаётся
  // таким даже после того, как типы подгрузились — кнопка «Создать» навсегда
  // заблокирована. Синхронизируем typeId с первым доступным типом.
  useEffect(() => {
    if (draft.typeId === "" && types.length > 0) {
      setDraft((d) => ({ ...d, typeId: types[0]!.id }));
    }
  }, [types, draft.typeId]);

  const createMutation = useMutation({
    mutationFn: () =>
      launchTypesApi.addCard(launchTypeId, {
        dayKey: draft.dayKey,
        typeId: draft.typeId,
        title: draft.title.trim(),
        preview: draft.preview,
        time: draft.time.trim() || null,
      }),
    onSuccess: onSaved,
  });

  const patchMutation = useMutation({
    mutationFn: (opts?: { force?: boolean }) => {
      if (state.kind !== "edit") throw new Error("no target");
      return launchTypesApi.patchCard(
        launchTypeId,
        state.card.id,
        {
          dayKey: draft.dayKey,
          typeId: draft.typeId,
          title: draft.title.trim(),
          preview: draft.preview,
          time: draft.time.trim() || null,
        },
        opts?.force || !baselineUpdatedAt
          ? undefined
          : { expectedUpdatedAt: baselineUpdatedAt },
      );
    },
    onSuccess: onSaved,
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        const current = (err.details as { current?: LaunchTypeCard } | undefined)?.current;
        if (current) {
          setConflict(current);
          return;
        }
      }
      setPatchError(err);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => {
      if (state.kind !== "edit") throw new Error("no target");
      return launchTypesApi.duplicateCard(launchTypeId, state.card.id);
    },
    onSuccess: onSaved,
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (state.kind !== "edit") throw new Error("no target");
      return launchTypesApi.removeCard(launchTypeId, state.card.id);
    },
    onSuccess: onSaved,
  });

  const isPending =
    createMutation.isPending ||
    patchMutation.isPending ||
    duplicateMutation.isPending ||
    deleteMutation.isPending;
  const canSubmit = draft.title.trim().length > 0 && Boolean(draft.typeId) && !isPending;
  const error =
    createMutation.error ||
    patchError ||
    duplicateMutation.error ||
    deleteMutation.error;

  const handleDuplicate = () => {
    if (state.kind !== "edit" || isPending) return;
    duplicateMutation.mutate();
  };

  const handleDelete = () => {
    if (state.kind !== "edit" || isPending) return;
    if (window.confirm(`Удалить карточку «${state.card.title}»?`)) {
      deleteMutation.mutate();
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    setPatchError(null);
    if (isNew) createMutation.mutate();
    else patchMutation.mutate(undefined);
  };

  const handleConflictKeepMine = () => {
    setPatchError(null);
    patchMutation.mutate({ force: true });
    setConflict(null);
  };
  const handleConflictTakeTheirs = () => {
    if (!conflict) return;
    setDraft({
      dayKey: conflict.dayKey,
      typeId: conflict.typeId,
      title: conflict.title,
      preview: conflict.preview,
      time: conflict.time ?? "",
    });
    setBaselineUpdatedAt(conflict.updatedAt);
    setConflict(null);
  };

  const activeType = types.find((t) => t.id === draft.typeId);
  const mdEditorKey = `md-${state.kind === "edit" ? state.card.id : "new"}-${draft.typeId}`;

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal modal-lg card-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          {activeType && (
            <span
              className="type-badge"
              style={{
                background: activeType.bg,
                color: activeType.text,
                marginRight: 8,
              }}
            >
              <span className="type-dot" style={{ background: activeType.color }} />
              {activeType.name}
            </span>
          )}
          <h2 style={{ flex: 1, minWidth: 0 }}>
            {isNew ? "Новая карточка шаблона" : "Карточка шаблона"}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Закрыть">
            <IcoClose size={16} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-row split">
            <div>
              <label className="form-label">День</label>
              <select
                className="form-input"
                value={draft.dayKey}
                onChange={(e) => setDraft((d) => ({ ...d, dayKey: e.target.value }))}
                disabled={readOnly}
              >
                {days.map((d, i) => (
                  <option key={d.id} value={d.id}>
                    День {i + 1} · {stageLabel(d.stage)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Тип карточки</label>
              <select
                className="form-input"
                value={draft.typeId}
                onChange={(e) => setDraft((d) => ({ ...d, typeId: e.target.value }))}
                disabled={readOnly}
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Заголовок</label>
            <input
              className="form-input"
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder="Например: Утреннее письмо с приглашением"
              disabled={readOnly}
              autoFocus
              maxLength={200}
            />
          </div>

          <div className="form-row">
            <label className="form-label">
              <IcoClock size={11} /> Время (необязательно)
            </label>
            <input
              className="form-input"
              value={draft.time}
              onChange={(e) => setDraft((d) => ({ ...d, time: e.target.value }))}
              placeholder="09:00"
              pattern="\d{2}:\d{2}"
              disabled={readOnly}
              style={{ maxWidth: 120 }}
            />
          </div>

          <div className="form-row">
            <label className="form-label">
              {activeType?.id === "t_note"
                ? "Заметка"
                : activeType?.id === "t_email"
                  ? "Текст письма / сообщения"
                  : "Текст"}
            </label>
            {readOnly ? (
              <div
                className="md-readonly-preview"
                dangerouslySetInnerHTML={{ __html: mdToHtml(draft.preview || "") as string }}
              />
            ) : (
              <MdEditor
                key={mdEditorKey}
                value={draft.preview}
                onChange={(md) => setDraft((d) => ({ ...d, preview: md }))}
                className="md-editor--compact"
              />
            )}
          </div>
        </div>

        <div className="modal-foot">
          {!isNew && !readOnly && (
            <>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={isPending}
              >
                <IcoTrash size={13} /> Удалить
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={handleDuplicate}
                disabled={isPending}
              >
                <IcoCopy size={13} /> Дублировать
              </button>
            </>
          )}
          <div className="foot-hint">
            {error ? (
              <span style={{ color: "#DC2626" }}>⚠ {(error as Error).message}</span>
            ) : !readOnly && types.length === 0 ? (
              <span style={{ color: "var(--text-muted)" }}>Загружаем типы карточек…</span>
            ) : null}
          </div>
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>
            {readOnly ? "Закрыть" : "Отмена"}
          </button>
          {!readOnly && (
            <button type="button" className="btn-primary" onClick={submit} disabled={!canSubmit}>
              <IcoCheck size={14} />
              {isPending ? "Сохраняем…" : isNew ? "Создать" : "Сохранить"}
            </button>
          )}
        </div>
      </div>
      {conflict && (
        <ConflictDialog
          title="Карточка шаблона была изменена"
          updatedAt={conflict.updatedAt}
          fields={buildLaunchTypeCardDiff(draft, conflict, types, days)}
          onKeepMine={handleConflictKeepMine}
          onTakeTheirs={handleConflictTakeTheirs}
          onClose={() => setConflict(null)}
          busy={patchMutation.isPending}
        />
      )}
    </ModalBackdrop>
  );
}

function stageLabel(s: string): string {
  if (s === "warmup") return "Прогрев";
  if (s === "sales") return "Эфиры";
  if (s === "close") return "Дожим";
  return s;
}

function buildLaunchTypeCardDiff(
  draft: { dayKey: string; typeId: string; title: string; preview: string; time: string },
  server: LaunchTypeCard,
  types: CardType[],
  days: LaunchTypeDay[],
): ConflictFieldDiff[] {
  const out: ConflictFieldDiff[] = [];
  if (draft.title.trim() !== server.title) {
    out.push({ label: "Заголовок", yours: draft.title.trim(), theirs: server.title });
  }
  if ((draft.preview ?? "") !== (server.preview ?? "")) {
    out.push({
      label: "Содержимое",
      yours: draft.preview ?? "",
      theirs: server.preview ?? "",
    });
  }
  if ((draft.time.trim() || null) !== (server.time ?? null)) {
    out.push({
      label: "Время",
      yours: draft.time.trim() || "—",
      theirs: server.time ?? "—",
    });
  }
  if (draft.dayKey !== server.dayKey) {
    const day = (k: string) => {
      const d = days.find((x) => x.id === k);
      if (!d) return k;
      const idx = days.indexOf(d);
      return `День ${idx + 1} · ${stageLabel(d.stage)}`;
    };
    out.push({ label: "День", yours: day(draft.dayKey), theirs: day(server.dayKey) });
  }
  if (draft.typeId !== server.typeId) {
    const typeName = (id: string) => types.find((t) => t.id === id)?.name ?? id;
    out.push({
      label: "Тип карточки",
      yours: typeName(draft.typeId),
      theirs: typeName(server.typeId),
    });
  }
  return out;
}
